import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, getCategoryBySlug } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = slug ? getCategoryBySlug(slug) : undefined;
  const articles = useMemo(() => (slug ? getArticlesByCategory(slug) : []), [slug]);

  useEffect(() => {
    if (currentCategory) {
      document.title = `${currentCategory.name} - 亚洲一级日本精选内容导航`;
    }
  }, [currentCategory]);

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen">
          <div className="container-main text-center py-20">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-4">分类未找到</h1>
            <Link to="/" className="text-primary-600 hover:text-primary-700 text-sm">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 min-h-screen">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 overflow-x-auto flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line"></i>
            </span>
            <span className="text-foreground-800 font-medium whitespace-nowrap">{currentCategory.name}</span>
          </nav>

          <div className="flex flex-col lg:flex-row gap-8">
            <aside className="lg:w-60 shrink-0">
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 lg:sticky lg:top-24">
                <h2 className="text-sm font-heading font-semibold text-foreground-900 mb-3">内容分类</h2>
                <nav className="space-y-1">
                  {categories.map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.slug}`}
                      className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                        cat.slug === slug
                          ? 'bg-primary-50 text-primary-700 font-medium'
                          : 'text-foreground-600 hover:bg-background-100 hover:text-primary-600'
                      }`}
                    >
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={`${cat.icon} text-xs`}></i>
                      </span>
                      <span className="flex-1 whitespace-nowrap">{cat.name}</span>
                      <span className="text-xs text-foreground-400">{cat.articleCount}</span>
                    </Link>
                  ))}
                </nav>
              </div>
            </aside>

            <div className="flex-1 min-w-0">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-600">{currentCategory.description}</p>
              </div>

              {articles.length === 0 ? (
                <p className="text-sm text-foreground-500 py-10 text-center">暂无文章</p>
              ) : (
                <div className="space-y-4">
                  {articles.map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group flex flex-col sm:flex-row bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300"
                    >
                      <div className="sm:w-44 shrink-0 aspect-[16/10] sm:aspect-auto overflow-hidden">
                        <img
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="flex-1 p-4 flex flex-col justify-between">
                        <div>
                          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-500 line-clamp-2">{article.excerpt}</p>
                        </div>
                        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-calendar-line"></i>
                            </span>
                            {article.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <span className="w-3 h-3 flex items-center justify-center">
                              <i className="ri-time-line"></i>
                            </span>
                            {article.readTime}
                          </span>
                          <div className="flex items-center gap-1 flex-wrap">
                            {article.tags.map((tag) => (
                              <span key={tag} className="text-xs text-foreground-400 bg-background-100 px-1.5 py-0.5 rounded">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}